/** The languages the funnel's UI is translated into (see `fi.json` / `en.json`). */
export type Language = 'fi' | 'en';

export const SUPPORTED_LANGUAGES: readonly Language[] = ['fi', 'en'];

/** Endonyms (each language's own name), shown untranslated in the language
 *  switcher so a customer always recognizes their language regardless of the
 *  currently active UI language. */
export const LANGUAGE_NAMES: Record<Language, string> = {
  fi: 'Suomi',
  en: 'English',
};

/** Fallback when nothing (URL hint, persisted choice, or browser preference)
 *  resolves to a supported language — the funnel's primary market is Finnish. */
export const DEFAULT_LANGUAGE: Language = 'fi';

/** localStorage key under which an explicit language choice is remembered across
 *  visits. */
export const STORED_LANGUAGE_KEY = 'imtFunnelLanguage';

/** Map an arbitrary hint — a `?lang=` value, a persisted choice, or a browser
 *  locale like `en-US` / `sv_SE` — onto a supported {@link Language}, or `null`
 *  when it maps to none. Case-insensitive; only the primary subtag is used. */
export function normalizeLanguage(raw: string | null | undefined): Language | null {
  const primary = raw?.trim().toLowerCase().split(/[-_]/, 1)[0];
  return primary && (SUPPORTED_LANGUAGES as readonly string[]).includes(primary)
    ? (primary as Language)
    : null;
}

/** Resolve the initial UI language from the available sources, in priority
 *  order: an explicit `?lang=`/`?language=` URL hint (deep-link intent) beats a
 *  previously persisted choice, which beats the browser's preferred languages;
 *  {@link DEFAULT_LANGUAGE} when none resolve. Mirrors the webshop giving a
 *  deep-linked (e.g. English) customer their language instead of always Finnish. */
export function resolveInitialLanguage(sources: {
  urlHint?: string | null;
  storedChoice?: string | null;
  browserLanguages?: readonly string[];
}): Language {
  const fromUrl = normalizeLanguage(sources.urlHint);
  if (fromUrl) return fromUrl;

  const fromStored = normalizeLanguage(sources.storedChoice);
  if (fromStored) return fromStored;

  for (const candidate of sources.browserLanguages ?? []) {
    const fromBrowser = normalizeLanguage(candidate);
    if (fromBrowser) return fromBrowser;
  }

  return DEFAULT_LANGUAGE;
}
